import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Grid,
  Box,
  IconButton,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from "@material-ui/core";
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  Schedule as ScheduleIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Settings as TestIcon,
  Info as InfoIcon
} from "@material-ui/icons";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import { getStandardButtonStyles } from "../../styles/standardButtons";

const useStyles = makeStyles((theme) => {
  const standardButtons = getStandardButtonStyles(theme);
  return {
  root: {
    padding: theme.spacing(3)
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2.5),
    borderRadius: 16,
    backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.03)" : "#FFFFFF",
    border: theme.palette.type === "dark" 
      ? `1px solid ${theme.palette.primary.main}` 
      : `1px solid ${theme.palette.primary.main}80`,
    boxShadow: theme.palette.type === "dark" 
      ? `0 0 12px ${theme.palette.primary.main}80` 
      : `0 0 10px ${theme.palette.primary.main}40`,
    transition: "all 0.3s ease",
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    color: theme.mode === "light" ? theme.palette.primary.main : "#FFFFFF",
    fontWeight: 600
  },
  settingItem: {
    marginBottom: theme.spacing(2)
  },
  messageField: {
    marginTop: theme.spacing(1)
  },
  timeField: {
    width: "100%",
    maxWidth: 360,
    "& .MuiInputLabel-root": {
      fontSize: "0.95rem",
      whiteSpace: "nowrap",
    },
    "& .MuiInputBase-root": {
      marginTop: theme.spacing(2.5),
    },
  },
  whatsappControl: {
    "& .MuiInputLabel-root": {
      fontSize: "0.95rem",
    },
    "& .MuiInputBase-root": {
      marginTop: theme.spacing(2.5),
    },
  },
  saveButton: {
    ...standardButtons.applyButton,
    padding: theme.spacing(1.2, 4),
    fontSize: "0.95rem",
  },
  testButton: {
    background: "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)",
    color: "white",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: 8,
    marginLeft: theme.spacing(1),
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 4px 12px rgba(255, 152, 0, 0.3)"
    }
  },
  expandButton: {
    transform: "rotate(0deg)",
    transition: theme.transitions.create("transform", {
      duration: theme.transitions.duration.shortest
    })
  },
  expandButtonOpen: {
    transform: "rotate(180deg)"
  },
  helpText: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5)
  },
  messagePreview: {
    backgroundColor: theme.palette.type === "dark" ? "rgba(0, 0, 0, 0.25)" : theme.palette.grey[100],
    padding: theme.spacing(2),
    borderRadius: 12,
    marginTop: theme.spacing(1.5),
    border: theme.palette.type === "dark"
      ? `1px solid ${theme.palette.primary.main}60`
      : `1px solid ${theme.palette.primary.main}40`,
    boxShadow: theme.palette.type === "dark" ? `0 0 8px ${theme.palette.primary.main}20` : "none",
  },
  variableChip: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    padding: "2px 8px",
    borderRadius: 4,
    fontSize: "0.75rem",
    fontFamily: "monospace",
    margin: "0 2px"
  },
  infoBox: {
    backgroundColor: theme.palette.type === "dark" ? "rgba(0, 0, 0, 0.25)" : "rgba(25, 118, 210, 0.08)",
    border: theme.palette.type === "dark"
      ? `1px solid ${theme.palette.primary.main}60`
      : `1px solid ${theme.palette.primary.main}40`,
    boxShadow: theme.palette.type === "dark" ? `0 0 8px ${theme.palette.primary.main}20` : "none",
    borderRadius: 12,
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5)
  },
  infoIcon: {
    color: theme.mode === "light" ? "#1976d2" : theme.palette.primary.main,
    fontSize: "1.25rem",
    marginTop: 2
  },
  infoContent: {
    flex: 1
  }
};
});

const BirthdaySettings = () => {
  const classes = useStyles();
  const { whatsApps } = useContext(WhatsAppsContext);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    user: true,
    contact: true,
    general: true
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data } = await api.get("/birthdays/settings");
      const loadedSettings = data.data;

      // Lista de mensagens padrão conhecidas para verificar
      const knownDefaults = [
        '🎉 Parabéns, {nome}! Hoje é seu dia especial! Desejamos muito sucesso e felicidade! ',
        '🎉 Parabéns, {nome}! Hoje é seu aniversário! Desejamos muito sucesso, saúde e felicidade! ✨',
        '🎉 Felicitaciones, {nome}! ¡Hoy es tu día especial! ¡Te deseamos mucho éxito y felicidad! ✨',
        '🎉 Felicitaciones, {nome}! ¡Hoy es tu cumpleaños! ¡Te deseamos mucho éxito, salud y felicidad! ✨',
        '🎉 Felicitaciones, {nome}! ¡Hoy es tu día especial! ¡Te deseamos mucho éxito y felicidad! ',
        '🎉 Felicitaciones, {nome}! ¡Hoy es tu cumpleaños! Te deseamos mucho éxito, salud y felicidad! ✨',
      ];

      // Se a mensagem vier vazia ou for igual a um dos padrões conhecidos, usar a tradução atual
      if (!loadedSettings.userBirthdayMessage || knownDefaults.some(msg => loadedSettings.userBirthdayMessage.includes(msg.trim()))) {
        loadedSettings.userBirthdayMessage = i18n.t("birthdaySettings.defaults.userMessage");
      }

      if (!loadedSettings.contactBirthdayMessage || knownDefaults.some(msg => loadedSettings.contactBirthdayMessage.includes(msg.trim()))) {
        loadedSettings.contactBirthdayMessage = i18n.t("birthdaySettings.defaults.contactMessage");
      }

      setSettings(loadedSettings);
    } catch (error) {
      toast.error(i18n.t("birthdaySettings.toasts.loadError"));
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/birthdays/settings", settings);
      toast.success(i18n.t("birthdaySettings.toasts.success"));
    } catch (error) {
      toast.error(i18n.t("birthdaySettings.toasts.error"));
    } finally {
      setSaving(false);
    }
  };

  const handleTestMessage = async (messageType) => {
    // Para teste, vamos pedir um contato válido
    const contactId = prompt(i18n.t("birthdaySettings.form.promptContactId"));
    if (!contactId) return;

    try {
      await api.post("/birthdays/test-message", {
        contactId: contactId, // Envia como string, o backend trata
        messageType
      });
      toast.success(i18n.t("birthdaySettings.toasts.testSuccess"));
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.error;
      if (errorMsg) {
        toast.error(errorMsg);
      } else {
        toast.error(i18n.t("birthdaySettings.toasts.testError"));
      }
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const renderMessagePreview = (message, type) => {
    if (!message) return null;

    let previewMessage = message;
    previewMessage = previewMessage.replace(
      /{nome}/g,
      `<span class="${classes.variableChip}">João Silva</span>`
    );
    previewMessage = previewMessage.replace(
      /{idade}/g,
      `<span class="${classes.variableChip}">30</span>`
    );

    return (
      <Box className={classes.messagePreview}>
        <Typography variant="caption" color="textSecondary">
          {i18n.t("birthdaySettings.info.preview")}
        </Typography>
        <Typography
          variant="body2"
          dangerouslySetInnerHTML={{ __html: previewMessage }}
        />
      </Box>
    );
  };

  if (loading) {
    return (
      <Box className={classes.root}>
        <Typography>{i18n.t("birthdaySettings.loading")}</Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {/* Configurações de Usuários */}
      <Paper className={classes.paper} variant="outlined">
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" className={classes.sectionTitle}>
            <PersonIcon />
            {i18n.t("birthdaySettings.sections.user")}
          </Typography>
          <IconButton
            className={`${classes.expandButton} ${expandedSections.user ? classes.expandButtonOpen : ""
              }`}
            onClick={() => toggleSection("user")}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>

        <Collapse in={expandedSections.user}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings?.userBirthdayEnabled || false}
                    onChange={(e) => handleSettingChange("userBirthdayEnabled", e.target.checked)}
                    color="primary"
                  />
                }
                label={i18n.t("birthdaySettings.form.userEnabled")}
              />
              <Typography className={classes.helpText}>
                {i18n.t("birthdaySettings.form.userEnabledHelp")}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings?.createAnnouncementForUsers || false}
                    onChange={(e) => handleSettingChange("createAnnouncementForUsers", e.target.checked)}
                    color="primary"
                    disabled={!settings?.userBirthdayEnabled}
                  />
                }
                label={i18n.t("birthdaySettings.form.userAnnouncement")}
              />
              <Typography className={classes.helpText}>
                {i18n.t("birthdaySettings.form.userAnnouncementHelp")}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={i18n.t("birthdaySettings.form.userMessage")}
                value={settings?.userBirthdayMessage || ""}
                onChange={(e) => handleSettingChange("userBirthdayMessage", e.target.value)}
                disabled={!settings?.userBirthdayEnabled}
                className={classes.messageField}
                helperText={i18n.t("birthdaySettings.form.userMessageHelp")}
              />
              {renderMessagePreview(settings?.userBirthdayMessage, "user")}
            </Grid>
          </Grid>
        </Collapse>
      </Paper>

      {/* Configurações de Contatos */}
      <Paper className={classes.paper} variant="outlined">
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" className={classes.sectionTitle}>
            <PhoneIcon />
            {i18n.t("birthdaySettings.sections.contact")}
          </Typography>
          <IconButton
            className={`${classes.expandButton} ${expandedSections.contact ? classes.expandButtonOpen : ""
              }`}
            onClick={() => toggleSection("contact")}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>

        <Collapse in={expandedSections.contact}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings?.contactBirthdayEnabled || false}
                    onChange={(e) => handleSettingChange("contactBirthdayEnabled", e.target.checked)}
                    color="primary"
                  />
                }
                label={i18n.t("birthdaySettings.form.contactEnabled")}
              />
              <Typography className={classes.helpText}>
                {i18n.t("birthdaySettings.form.contactEnabledHelp")}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label={i18n.t("birthdaySettings.form.contactMessage")}
                value={settings?.contactBirthdayMessage || ""}
                onChange={(e) => handleSettingChange("contactBirthdayMessage", e.target.value)}
                disabled={!settings?.contactBirthdayEnabled}
                className={classes.messageField}
                helperText={i18n.t("birthdaySettings.form.contactMessageHelp")}
              />
              {renderMessagePreview(settings?.contactBirthdayMessage, "contact")}

              {settings?.contactBirthdayEnabled && (
                <Button
                  variant="contained"
                  className={classes.testButton}
                  startIcon={<TestIcon />}
                  onClick={() => handleTestMessage("contact")}
                  size="small"
                  style={{ marginTop: 8 }}
                >
                  {i18n.t("birthdaySettings.form.test")}
                </Button>
              )}
            </Grid>
          </Grid>
        </Collapse>
      </Paper>

      {/* Configurações Gerais */}
      <Paper className={classes.paper} variant="outlined">
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" className={classes.sectionTitle}>
            <ScheduleIcon />
            {i18n.t("birthdaySettings.sections.general")}
          </Typography>
          <IconButton
            className={`${classes.expandButton} ${expandedSections.general ? classes.expandButtonOpen : ""
              }`}
            onClick={() => toggleSection("general")}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>

        <Collapse in={expandedSections.general}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                type="time"
                label={i18n.t("birthdaySettings.form.generalTime")}
                value={settings?.sendBirthdayTime || "09:00:00"}
                onChange={(e) => {
                  // Converter HH:MM para HH:MM:SS para compatibilidade com backend
                  const timeValue = e.target.value;
                  const formattedTime = timeValue ? `${timeValue}:00` : "09:00:00";
                  handleSettingChange("sendBirthdayTime", formattedTime);
                }}
                className={classes.timeField}
                InputLabelProps={{ shrink: true }}
                helperText={i18n.t("birthdaySettings.form.generalTimeHelp")}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth className={classes.whatsappControl}>
                <InputLabel shrink>{i18n.t("birthdaySettings.form.generalWhatsapp")}</InputLabel>
                <Select
                  value={settings?.whatsappId || ""}
                  onChange={(e) => handleSettingChange("whatsappId", e.target.value || null)}
                  label={i18n.t("birthdaySettings.form.generalWhatsapp")}
                  disabled={!settings?.contactBirthdayEnabled}
                >
                  <MenuItem value="">
                    <em>{i18n.t("birthdaySettings.form.generalWhatsappSelect")}</em>
                  </MenuItem>
                  {Array.isArray(whatsApps) && whatsApps.length > 0 ? (
                    whatsApps.map((whatsapp) => (
                      <MenuItem key={whatsapp.id} value={whatsapp.id}>
                        <Box display="flex" alignItems="center">
                          <Box
                            width={8}
                            height={8}
                            borderRadius="50%"
                            bgcolor={whatsapp.status === "CONNECTED" ? "success.main" : "error.main"}
                            mr={1}
                          />
                          {whatsapp.name} ({whatsapp.status})
                        </Box>
                      </MenuItem>
                    ))
                  ) : null}
                </Select>
                <Typography className={classes.helpText}>
                  {i18n.t("birthdaySettings.form.generalWhatsappHelp")}
                </Typography>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings?.createAnnouncementForUsers || false}
                    onChange={(e) => handleSettingChange("createAnnouncementForUsers", e.target.checked)}
                    color="primary"
                  />
                }
                label={i18n.t("birthdaySettings.form.internalAnnouncement")}
              />
              <Typography className={classes.helpText}>
                {i18n.t("birthdaySettings.form.internalAnnouncementHelp")}
              </Typography>
            </Grid>
          </Grid>

          <Box className={classes.infoBox}>
            <InfoIcon className={classes.infoIcon} />
            <Box className={classes.infoContent}>
              <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 8 }}>
                {i18n.t("birthdaySettings.info.variables")}
              </Typography>
              <Typography variant="body2" component="div">
                • <code>{"{nome}"}</code> - {i18n.t("birthdaySettings.info.variableName")}<br />
                • <code>{"{idade}"}</code> - {i18n.t("birthdaySettings.info.variableAge")}
              </Typography>
            </Box>
          </Box>
        </Collapse>
      </Paper>

      {/* Botão Salvar */}
      <Box textAlign="center" mt={3}>
        <Button
          variant="contained"
          className={classes.saveButton}
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          size="large"
        >
          {saving ? i18n.t("birthdaySettings.form.saving") : i18n.t("birthdaySettings.form.save")}
        </Button>
      </Box>
    </Box>
  );
};

export default BirthdaySettings;
